import { StarOutlineRounded, StarRounded } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';

import { statsData } from './data';
import muiStyles from './muiStyles';
import styles from './styles.module.css';

import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BusinessesService } from 'service/businesses.service';
import zeplinColor from 'theme/Color';
import { PlatformInsightData } from '../types';

const Header = ({ data }: { data: PlatformInsightData }) => {
  const { id } = useParams();
  const { logoUrl, name, website, isFollowed, isTargeted, metrics, businessId, zoominfoCompanyId } =
    data;
  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();
  const [isFollow, setIsFollow] = useState(isFollowed);
  const [isTarget, setIsTarget] = useState(isTargeted);
  const targetTextContent = isTarget ? 'Remove' : 'Target';

  const targetId = useRef(businessId);

  const toggleTarget = async () => {
    try {
      if (isTarget) {
        startOperation('delete', 'Removing Account...');
        await BusinessesService.removeTarget(targetId.current ?? '');
        targetId.current = zoominfoCompanyId;
        completeOperation('delete', 'Account Removed');
      } else {
        startOperation('upload', 'Targeting Account...');
        const res = await BusinessesService.addTarget({
          zoominfoCompanyId: String(targetId.current),
          category: 'Brand',
        });
        targetId.current = res.business.businessId;
        completeOperation('upload', 'Account Targeted');
      }
      setIsTarget(!isTarget);
    } catch (error) {
      console.error('Failed to update target status', error);
      completeOperation('upload', String(error), true);
    }
  };

  const handleFollowAccount = async () => {
    if (!id) return;

    if (!isFollow) {
      startOperation('upload', 'Following Account...');
      await BusinessesService.followBusiness(targetId.current);
      setIsFollow(!isFollow);
      completeOperation('upload', 'Account Followed');
    } else {
      startOperation('delete', 'Unfollow Account...');
      await BusinessesService.unfollowBusiness(targetId.current);
      setIsFollow(!isFollow);
      completeOperation('delete', 'Account Unfollowed');
    }
  };

  return (
    <Box sx={muiStyles.container}>
      <Box sx={muiStyles.companyIdentity}>
        <img
          src={logoUrl}
          alt='logo'
          loading='lazy'
          className={styles.logo}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = '/assets/zoominfo-fallback.svg';
          }}
        />

        <Box sx={muiStyles.companyMetaContainer}>
          <Box sx={muiStyles.companyMeta}>
            <Typography variant='responsiveHeading1'>{name}</Typography>
            <Button onClick={toggleTarget} sx={muiStyles.targetButton}>
              <Typography variant='responsiveButtonSmall'>{targetTextContent} Account</Typography>
            </Button>
            <IconButton
              sx={{
                ...muiStyles.followButton,
                ':hover': {
                  bgcolor: isFollow ? zeplinColor.Neutral4 : zeplinColor.Neutral10,
                },
                bgcolor: isFollow ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
              }}
              onClick={handleFollowAccount}
            >
              {isFollow ? (
                <StarRounded fontSize='small' sx={{ color: zeplinColor.BasicBlack }} />
              ) : (
                <StarOutlineRounded fontSize='small' sx={{ color: zeplinColor.Neutral1 }} />
              )}
            </IconButton>
          </Box>

          <Box sx={muiStyles.websiteContainer}>
            <img
              src={'/assets/zoominfo-website.svg'}
              alt='website'
              loading='lazy'
              className={styles.website}
            />
            <a
              href={'https://' + website}
              className={styles.link}
              target='_blank'
              rel='noopener noreferrer'
            >
              <Typography variant='responsiveBody3'>{website}</Typography>
            </a>
          </Box>
        </Box>
      </Box>

      <Box sx={muiStyles.stats}>
        {metrics &&
          statsData.map((item) => {
            const { id, label, format } = item;
            const formattedValue = format ? format(metrics[id]) : metrics[id];
            const valueColor =
              label === 'Success Rate' ? zeplinColor.Success : zeplinColor.Neutral3;

            return (
              <Box sx={muiStyles.statContainer}>
                <Typography variant='responsiveHeading4' color={valueColor}>
                  {formattedValue ?? 'N/A'}
                </Typography>
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral3}>
                  {label}
                </Typography>
              </Box>
            );
          })}
      </Box>

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default Header;
